import { useMemo } from 'react';
import { useSyberryTodayApi } from 'infrastructure/api';
import { SYBERRY_TODAY_API_ENDPOINTS } from './endpoints';
import { readJsonResponse, readJsonResponseOrFallback, withSyberryTodayApiError } from './response';
import { useStableSyberryTodayApiTransport, type SyberryTodayApiTransport } from './transport';
import {
  type SyberryTodayApiCheckinStatusResponse,
  type SyberryTodayApiConfirmCheckinRequest,
  type SyberryTodayApiCreateTimeEntryRequest,
  type SyberryTodayApiResponse,
  type SyberryTodayApiUpdateIssueCategoryRequest,
  type SyberryTodayApiUpdateIssueOrderRequest,
} from './types';

export { SYBERRY_TODAY_ALLOCATION_API_ENDPOINTS, SYBERRY_TODAY_API_ENDPOINTS } from './endpoints';
export { parseSyberryTodayApiError } from './response';
export { useStableSyberryTodayApiTransport } from './transport';
export type { SyberryTodayApiTransport } from './transport';

export type SyberryTodayApiClient = {
  getSyberryToday: () => Promise<SyberryTodayApiResponse>;
  updateIssueCategory: (
    issueId: string,
    request: SyberryTodayApiUpdateIssueCategoryRequest,
  ) => Promise<{ success: true }>;
  updatePersonalBacklogOrder: (
    request: SyberryTodayApiUpdateIssueOrderRequest,
  ) => Promise<SyberryTodayApiUpdateIssueOrderRequest & { success: true }>;
  updateQueueIssueOrder: (
    queueId: number,
    request: SyberryTodayApiUpdateIssueOrderRequest,
  ) => Promise<SyberryTodayApiUpdateIssueOrderRequest & { success: true }>;
  confirmCheckin: (request: SyberryTodayApiConfirmCheckinRequest) => Promise<SyberryTodayApiCheckinStatusResponse>;
  skipCheckin: () => Promise<SyberryTodayApiCheckinStatusResponse>;
  postponeCheckin: () => Promise<SyberryTodayApiCheckinStatusResponse>;
  createIssueTimeEntry: (issueId: string, request: SyberryTodayApiCreateTimeEntryRequest) => Promise<{ success: true }>;
};

export const createSyberryTodayApiClient = (transport: SyberryTodayApiTransport): SyberryTodayApiClient => ({
  getSyberryToday: () =>
    withSyberryTodayApiError(async () =>
      readJsonResponse<SyberryTodayApiResponse>(await transport.sendGetRequest(SYBERRY_TODAY_API_ENDPOINTS.today)),
    ),

  updateIssueCategory: (issueId, request) =>
    withSyberryTodayApiError(async () =>
      readJsonResponseOrFallback<{ success: true }>(
        await transport.sendPutRequest(SYBERRY_TODAY_API_ENDPOINTS.issueCategory(issueId), request),
        { success: true },
      ),
    ),

  updatePersonalBacklogOrder: request =>
    withSyberryTodayApiError(async () =>
      readJsonResponseOrFallback<SyberryTodayApiUpdateIssueOrderRequest & { success: true }>(
        await transport.sendPutRequest(SYBERRY_TODAY_API_ENDPOINTS.personalBacklogOrder, request),
        { success: true, positions: request.positions },
      ),
    ),

  updateQueueIssueOrder: (queueId, request) =>
    withSyberryTodayApiError(async () =>
      readJsonResponseOrFallback<SyberryTodayApiUpdateIssueOrderRequest & { success: true }>(
        await transport.sendPutRequest(SYBERRY_TODAY_API_ENDPOINTS.queueIssueOrder(queueId), request),
        { success: true, positions: request.positions },
      ),
    ),

  confirmCheckin: request =>
    withSyberryTodayApiError(async () =>
      readJsonResponse<SyberryTodayApiCheckinStatusResponse>(
        await transport.sendPostRequest(SYBERRY_TODAY_API_ENDPOINTS.confirmCheckin, request),
      ),
    ),

  skipCheckin: () =>
    withSyberryTodayApiError(async () =>
      readJsonResponse<SyberryTodayApiCheckinStatusResponse>(
        await transport.sendPostRequest(SYBERRY_TODAY_API_ENDPOINTS.skipCheckin),
      ),
    ),

  postponeCheckin: () =>
    withSyberryTodayApiError(async () =>
      readJsonResponse<SyberryTodayApiCheckinStatusResponse>(
        await transport.sendPostRequest(SYBERRY_TODAY_API_ENDPOINTS.postponeCheckin),
      ),
    ),

  createIssueTimeEntry: (issueId, request) =>
    withSyberryTodayApiError(async () =>
      readJsonResponseOrFallback<{ success: true }>(
        await transport.sendPostRequest(SYBERRY_TODAY_API_ENDPOINTS.issueTimeEntries(issueId), request),
        { success: true },
      ),
    ),
});

export const useSyberryTodayApiClient = (): SyberryTodayApiClient => {
  const transport = useStableSyberryTodayApiTransport(useSyberryTodayApi());

  return useMemo(() => createSyberryTodayApiClient(transport), [transport]);
};
